import { createContext, createElement, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from './supabase'
import { useAuth } from './AuthContext'
import { romeToday } from './romeTime'

// Maglie speciali di lega (iridata, europea, leader della Coppa del Mondo).
// Chi le ha oggi sta in fact_user_special_jersey (is_current). Si legge una volta per lega e si tiene in cache:
// sono poche righe (un detentore per maglia). L'avatar le mostra solo nella lega attiva.
// Priorita': se un giocatore ha piu' maglie speciali nella stessa lega, si mostra la prima di questa lista
export const SPECIAL_PRIORITY = ['world_champion', 'european_champion', 'worldcup_leader']
const rank = (code) => { const i = SPECIAL_PRIORITY.indexOf(code); return i < 0 ? 99 : i }

// Le maglie dipendono dal tempo: nella classifica di una gara passata l'avatar veste la maglia che aveva ALLORA, non
// quella di oggi. Le schermate che mostrano una gara/tappa avvolgono i loro avatar in
// <JerseyAsOf date="AAAA-MM-GG" stageId={id_tappa}> (data e tappa mostrate); senza provider, o con una data di oggi o
// futura, valgono le maglie attuali. Con stageId la maglia si legge da fact_user_stage_result (fotografata alla pubblicazione
// della tappa); senza, o se la funzione non risponde, si calcola alla data (fn_special_jerseys_at).
// La maglia di una gara si veste dal giorno DOPO la fine della gara che l'ha assegnata.
const AsOfContext = createContext(null)
export function JerseyAsOf({ date, stageId = null, children }) {
  const d = typeof date === 'string' && date.length >= 10 ? date.slice(0, 10) : null
  const value = useMemo(() => (d ? { date: d, stageId } : null), [d, stageId])
  return createElement(AsOfContext.Provider, { value }, children)
}

const STALE_MS = 5 * 60 * 1000
const cache = new Map()        // chiave → { at, map: Map(id_utente → [{ code, awardedAt }]) }; chiave = id_lega, o "id_lega@data"
const inflight = new Set()
const listeners = new Set()

const keyOf = (leagueId, asOf, stageId) => stageId ? `${leagueId}#${stageId}` : asOf ? `${leagueId}@${asOf}` : leagueId

function toMap(rows) {
  const map = new Map()   // id_utente → [{ code, awardedAt }] ordinato per priorita'
  rows.forEach(r => {
    const list = map.get(r.id_user) ?? []
    list.push({ code: r.jersey_code, awardedAt: r.awarded_at })
    map.set(r.id_user, list.sort((a, b) => rank(a.code) - rank(b.code)))
  })
  return map
}

async function load(leagueId, asOf, stageId) {
  const key = keyOf(leagueId, asOf, stageId)
  const hit = cache.get(key)
  // il passato non cambia: una volta letto non si riscarica; le maglie attuali invece invecchiano
  const fresh = hit && (asOf ? hit.ok : Date.now() - hit.at < STALE_MS)
  if (!leagueId || inflight.has(key) || fresh) return
  inflight.add(key)
  try {
    let rows
    if (asOf) {
      let res = stageId ? await supabase.rpc('fn_stage_special_jerseys', { p_id_league: leagueId, p_id_stage: stageId }) : null
      if (res && !res.error) {
        rows = (res.data ?? []).map(r => ({ id_user: r.id_user, jersey_code: r.jersey_code, awarded_at: null }))
      } else {
        res = await supabase.rpc('fn_special_jerseys_at', { p_id_league: leagueId, p_date: asOf })
        if (res.error) throw res.error
        rows = (res.data ?? []).map(r => ({ id_user: r.id_user, jersey_code: r.jersey_code, awarded_at: r.since }))
      }
    } else {
      const { data, error } = await supabase
        .from('fact_user_special_jersey').select('id_user, jersey_code, awarded_at')
        .eq('id_league', leagueId).eq('is_current', true)
      if (error) throw error
      rows = data ?? []
    }
    cache.set(key, { at: Date.now(), ok: true, map: toMap(rows) })
  } catch {
    // errore di rete: tengo quello che c'era e riprovo tra 30 secondi
    cache.set(key, { at: Date.now() - STALE_MS + 30000, ok: false, map: hit?.map ?? new Map() })
  } finally {
    inflight.delete(key)
    listeners.forEach(fn => fn())
  }
}

// Codice della maglia speciale che l'utente veste nella lega attiva (es. 'world_champion'), o null
export function useSpecialJersey(userId) {
  return useSpecialJerseyInfo(userId)?.code ?? null
}

// Come sopra, con la data di assegnazione: { code, awardedAt } oppure null (la maglia di priorita' piu' alta)
export function useSpecialJerseyInfo(userId) {
  return useSpecialJerseyList(userId)[0] ?? null
}

const NONE = []
// Tutte le maglie speciali che l'utente veste nella lega attiva, in ordine di priorita': [{ code, awardedAt }]
export function useSpecialJerseyList(userId) {
  const { leagueId } = useAuth()
  const ctx = useContext(AsOfContext)
  const asOf = ctx && ctx.date < romeToday() ? ctx.date : null   // oggi o futuro = attuali
  const stageId = asOf ? ctx.stageId : null
  const [, bump] = useState(0)
  useEffect(() => {
    const fn = () => bump(v => v + 1)
    listeners.add(fn)
    load(leagueId, asOf, stageId)
    return () => { listeners.delete(fn) }
  }, [leagueId, asOf, stageId])
  // mentre la maglia di quella data si carica: nessuna maglia (meglio che mostrare quella di oggi, che e' sbagliata)
  return cache.get(keyOf(leagueId, asOf, stageId))?.map.get(userId) ?? NONE
}
